import { createRoot } from "react-dom/client"
import { ApolloProvider } from "@apollo/client/react"
import { BrowserRouter } from "react-router"

import "./index.css"
import { clientMain, initCachePersist } from "./graphql/client/client"
import { ErrorBoundary } from "@/components/errorBoundary"
import authSupport from "@/features/auth-support"
import { logoutUser } from "@/features/auth-support/fnLogout"

import { LanguageProvider } from "./locales"
import { addLogDebug } from "./model/logger"
import { enableAlwaysHoverOnTouchDevices } from "./model/enableAlwaysHoverOnTouchDevices"
import { initGA } from "./model/ga"
import { initClarity } from "./model/clarity"
import { initMetrika } from "./model/metrika"
import "./model/dateSetup"
import { initMixpanel } from "./model/mixpanel"
import { RouterBridge } from "./model/RouterBridge"
import config from "./config"
import { setLogLvL } from "./model/LogLevel"
import { initSentry } from "./model/sentry"
import { watchErrorGlobal } from "./model/watchError.fn"
import { NotificationProvider } from "@/app/providers/NotificationProvider"
import { AppShell } from "@/app/AppShell"
import { initImpersonation } from "./model/impersonation"
import { UpdateProvider } from "@/components/UpdateNotification"
import { AutoUpdateOnNavigate } from "@/components/UpdateNotification/AutoUpdateOnNavigate"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { applyServerChoiceRedirect } from "@/features/server-select"
import { LOGOUT_PAGE } from "@shared/constants/navigation"
import { initTheme } from "@shared/theme"

// Manual server selection (RF access): honor a persisted eu./ru. choice before
// anything else boots. Only the Auto host (app.) ever redirects; path, query
// and hash are preserved, so every init below re-runs on the chosen origin.
applyServerChoiceRedirect()

// Initialize impersonation from URL hash (must be early, before Apollo)
initImpersonation()

// Handle /logout route before app initialization
// This is needed because UserProvider would redirect unauthenticated users to login,
// creating a redirect loop if auth service returns to /logout
if (window.location.pathname === LOGOUT_PAGE) {
  localStorage.removeItem(STORAGE_KEYS.BIND_TRAINER_LINK)
  // logoutUser clears the remaining tokens + Apollo cache and ends the session —
  // in-app (Kratos logout flow → /auth/login) when the flag is on, external otherwise.
  logoutUser()
}

watchErrorGlobal()

// index.html already put the theme on <html>; this keeps it current (settings, the OS, other tabs).
initTheme()

setLogLvL()
addLogDebug("Application version", config.BUILD_VERSION)
authSupport.init()
enableAlwaysHoverOnTouchDevices()
initGA()
initMixpanel()
initClarity()
initMetrika()

initSentry()

const container = document.getElementById("root")
if (container === null) {
  throw new Error("React root element doesn't exist!")
}

// Clean up any leftover persisted cache (persistence is disabled).
// Deliberately NOT awaited before render: IndexedDB calls can hang
// indefinitely (known WebKit behavior under storage pressure / restored
// tabs), nothing reads these keys, and blocking the first render on them
// risks a permanent white screen.
void initCachePersist()

createRoot(container).render(
  <ApolloProvider client={clientMain}>
    <UpdateProvider>
      <LanguageProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <RouterBridge />
            <AutoUpdateOnNavigate />
            <NotificationProvider>
              <AppShell />
            </NotificationProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </LanguageProvider>
    </UpdateProvider>
  </ApolloProvider>,
)
